// The andon API surface, one function per BFF endpoint — lifted from the Andon
// app's client, minus the session, the wall display and the TV's GIF list.
//
// Deliberately boring: no request building, no query assembly, no model names.
// Everything the andon module can be asked for is on this object, which makes
// the list of what the merged app can do with an andon fit on one screen.
//
// It rides on the shop floor's own `http` helper, not Andon's: one sign-in,
// one cookie, one CSRF header (`X-SF-Api`). The routes are the Andon server's
// routes ported under the shop floor BFF's `/api/andon/*`, so the paths are the
// same ones the Andon client has always called — only the origin changed.
//
// While the BFF's andon module is dormant every call here answers 404. Nothing
// in the app calls any of them unless `capabilities.andon.preview` is on, which
// is the switch the server flips in the same breath as waking the module.

import { http } from '@/api/http';
import type {
  AlertDetailVM,
  AlertEventVM,
  AlertTypeKey,
  AndonAlertVM,
  AndonFlowlineVM,
  AndonPodVM,
  AndonWorkcentreVM,
  BoardVM,
  CausesVM,
  DefectOption,
  FloorVM,
  InsightVM,
  NotificationVM,
  OperatorHomeVM,
  PreRaiseAnswer,
  PreRaiseQuestion,
  PushServerView,
  PushTestVM,
  RosterPerson,
  RoutingPreviewVM,
  Stars,
  SubjectOption,
  TasksVM,
  ThreadAttachmentVM,
  ThreadMessageVM,
} from './types';

/**
 * WHERE A PERSON'S FACE COMES FROM. Undefined when we have no handle on them.
 *
 * Two handles because there are two kinds of person on this app: an Odoo
 * account (`uid`) for anybody who signs in, and an `hr.employee` for an
 * operator signed in by PIN, who has no account at all. The route takes
 * either and answers 404 whenever there is no photograph, and the avatar
 * draws initials on 404 exactly as it draws them when this returns undefined.
 * The two paths to initials are the same path, which is why nothing here
 * tries to predict which one it will be.
 *
 * Kept beside the shop floor's `employeeAvatarUrl` rather than replacing it:
 * that one is the employee half only, and every existing avatar reads it.
 */
export const andonAvatarUrl = (who: {
  uid?: number | null | undefined;
  employeeId?: number | null | undefined;
}): string | undefined => {
  // The employee first: it names the operator holding the device rather than
  // the account it is signed in as.
  if (who.employeeId) return `/api/avatar?employee=${who.employeeId}`;
  if (who.uid) return `/api/avatar?uid=${who.uid}`;
  return undefined;
};

export const andonApi = {
  // ── station ──────────────────────────────────────────────────────────────
  /**
   * Tell the session which station this device is at.
   *
   * The andon module reads the station off the session for a raise, the way
   * the Andon app did, rather than trusting whatever the client sends with
   * the raise itself. The shop floor remembers the station on the device;
   * this is what makes the server agree.
   */
  setStation: (workcentreId: number, flowlineId: number | null) =>
    http.post<{ ok: true }>('/api/session/station', { workcentreId, flowlineId }),

  // ── operator ─────────────────────────────────────────────────────────────
  home: () => http.get<OperatorHomeVM>('/api/andon/home'),

  /** The whole floor. Searching and filtering it happens in the browser. */
  floor: (scope: 'open' | 'all') => http.get<FloorVM>(`/api/andon/floor?scope=${scope}`),

  raise: (input: {
    stars: Stars;
    type: AlertTypeKey;
    productionId?: number | null;
    words?: string | null;
    /** Round 0's answers, in the order they were put. */
    answers?: Array<{ question: string; answer: string }>;
    /**
     * LOGINS the operator picked with an @, never names parsed out of `words`.
     *
     * They join the first responders: told on creation, added as followers, and
     * shown in the who-gets-told block before the raise so being told is never a
     * surprise. Names are not unique and a typo must not notify the wrong Ben,
     * which is why the picker's login list is what travels and the text is only
     * for humans to read.
     */
    mentions?: string[];
  }) => http.post<{ alert: AndonAlertVM; raiseMessageId: number | null }>('/api/andon/raise', input),

  alert: (id: number) =>
    http.get<{ alert: AlertDetailVM; thread: ThreadMessageVM[]; events: AlertEventVM[] }>(
      `/api/andon/alert/${id}`,
    ),

  answer: (alertId: number, question: string, answer: string) =>
    http.post<{ alert: AndonAlertVM }>('/api/andon/answer', { alertId, question, answer }).then((r) => r.alert),

  /**
   * "It has stopped now."
   *
   * Only ever upward, and only the operator who raised it. This is the event
   * the old design could not record at all — urgency was frozen at raise, so
   * the only way to say a warning had become a stoppage was to raise a second
   * andon and split the history of one problem across two records.
   */
  raiseUrgency: (alertId: number, stars: 2 | 3) =>
    http.post<{ alert: AndonAlertVM }>('/api/andon/urgency', { alertId, stars }).then((r) => r.alert),

  /**
   * "It'll stop in about an hour."
   *
   * `minutes` null is the "not sure" chip, resolved server-side to the end of
   * the shift against the work centre's own calendar. It is a real answer, not
   * a missing one: an operator made to pick a number picks one, and a
   * fabricated estimate is worse than none because a clock acts on it.
   *
   * Re-estimating is expected — pushing a stop out is what the prompt is for.
   */
  estimateStop: (alertId: number, minutes: 20 | 60 | 120 | null) =>
    http.post<{ alert: AndonAlertVM }>('/api/andon/stop-estimate', { alertId, minutes }).then((r) => r.alert),

  /**
   * Every MO, pod type and project the operator could mean — about five
   * hundred rows, fetched once and filtered in the browser.
   *
   * Whole rather than queried per keystroke: on a factory network with gloves
   * on, a round trip between characters is not a search box. It is the same
   * answer for everybody, so react-query caches it across alerts.
   */
  subjects: () => http.get<{ options: SubjectOption[]; at: number }>('/api/andon/subjects'),

  /**
   * The defect vocabulary — 297 rows, the same for everybody, cached for an
   * hour. Three of them are ranked onto chips per raise; this is what makes
   * the other 294 reachable.
   */
  defects: () => http.get<{ options: DefectOption[]; at: number }>('/api/andon/defects'),

  /** The equipment register, by what was typed into the machine box. */
  equipment: (q: string) =>
    http.get<{ options: Array<{ label: string; detail: string | null }> }>(
      `/api/andon/equipment?q=${encodeURIComponent(q)}`,
    ),

  /**
   * The round-0 questions that could not come down with home, because they are
   * RANKED and ranking needs the pod.
   *
   * The only round trip in the raise flow. It is made while the operator is
   * answering the identity question, so it overlaps a tap rather than
   * following one — and if it is slow, or fails, the flow simply asks one
   * question fewer and the card asks it a few seconds later with the same
   * chips. Nothing waits on this.
   *
   * Sends the ANSWERS, not a subject. "the meriton one" is only an MO to
   * something that can read Odoo, and a client that sent ids would be a client
   * that could rank an andon against a pod nobody selected.
   */
  ask: (input: { type: AlertTypeKey; productionId?: number | null; answers: PreRaiseAnswer[] }) =>
    http.post<{ questions: PreRaiseQuestion[] }>('/api/andon/ask', input).then((r) => r.questions),

  /** Correct the measured downtime. The operator was standing there. */
  stateDowntime: (alertId: number, minutes: number) =>
    http.post<{ ok: true }>('/api/andon/downtime', { alertId, minutes }),

  /**
   * Say something on the thread.
   *
   * `mentions` are the LOGINS the sender picked, never names parsed out of the
   * body. Being named subscribes you to the alert as well as pushing you this
   * message, so it is worth being sure who was meant.
   */
  message: (alertId: number, body: string, mentions?: string[], attachmentIds?: number[]) =>
    http.post<{ ok: true; messageId: number | null }>('/api/andon/message', {
      alertId,
      body,
      ...(mentions?.length ? { mentions } : {}),
      ...(attachmentIds?.length ? { attachmentIds } : {}),
    }),

  /**
   * A file that finished uploading after its message was already sent.
   *
   * Send stopped waiting for uploads — a 40MB clip is minutes on a factory
   * uplink and the sentence typed beside it is the urgent half. So the message
   * goes at once with whatever has landed, and each batch that settles joins
   * THAT message here rather than turning up as a separate wordless bubble
   * with nothing to say which sentence it belonged to.
   */
  attachToMessage: (alertId: number, messageId: number, attachmentIds: number[]) =>
    http.post<{ ok: true; attached: number }>('/api/andon/message/attach', { alertId, messageId, attachmentIds }),

  /**
   * THE CLOSE. One call, because the card is one screen with one button, and
   * the only way an andon is done.
   *
   * A partial save is a state nobody asked for: a close that recorded the
   * words and lost the cause looks answered to every count that reads it,
   * which is strictly worse than one never given. Odoo refuses the cause
   * missing, and the reason missing on a class that has them.
   *
   * There is no `minutes` here and there must never be. The delay is computed
   * by Odoo against the work centre's own shift calendar — that single choice
   * is most of the 731x gap between ticket-hours and real downtime in the
   * history, and exposing the figure as an input hands it straight back. What
   * CAN be corrected is the two timestamps, which flips the basis to STATED.
   */
  close: (input: {
    alertId: number;
    text: string;
    categoryId: number;
    subcategoryId?: number | null;
    causeWorkcentreId?: number | null;
    stoppedAt?: number | null;
    restartedAt?: number | null;
    productionIds?: number[];
    stars?: 3;
    mentions?: string[];
  }) => http.post<{ alert: AndonAlertVM }>('/api/andon/close', input).then((r) => r.alert),

  /**
   * The offer after a close: settle the quality alert this andon carries too.
   * Quality's approver rule decides in Odoo; a refusal comes back as its own
   * sentence naming the team.
   */
  closeQualityAlert: (alertId: number) =>
    http.post<{ alert: AndonAlertVM }>('/api/andon/close-quality', { alertId }).then((r) => r.alert),

  /**
   * The cause list: classes, each with the reasons under it.
   *
   * Cached hard. Around seventy rows that change only when somebody edits the
   * config, and the card has to be answerable the instant it opens — a round
   * trip between tapping a class and seeing its reasons is exactly the gap
   * that makes people pick whatever is already on screen.
   */
  causes: () => http.get<CausesVM>('/api/andon/causes'),

  /**
   * Search GIFs. The browser never meets Giphy — the BFF holds the key, runs
   * the search, and proxies the previews, so the CSP keeps allowing no
   * third-party origin at all.
   *
   * GIFs stay in the merge: the decision was to keep them.
   */
  gifs: (term: string) =>
    http
      .get<{ gifs: Array<{ id: string; title: string; width: number; height: number }> }>(
        `/api/andon/gifs?term=${encodeURIComponent(term)}`,
      )
      .then((r) => r.gifs),

  /** Where one preview's bytes live. Same-origin, so an <img> just works. */
  gifPreviewUrl: (id: string) => `/api/andon/gifs/${encodeURIComponent(id)}/preview`,

  /**
   * Pick one. It comes back as an ordinary attachment, because that is what it
   * becomes: the bytes are copied into Odoo rather than linked, so the thread
   * still renders when the Giphy URL has rotted.
   */
  gifAttach: (alertId: number, gifId: string) =>
    http.post<{ ok: true; attachment: ThreadAttachmentVM }>('/api/andon/gif', { alertId, gifId }),

  /**
   * Tap an emoji at somebody's message.
   *
   * ONE CALL, AND IT TOGGLES. The chip on screen may be seconds old, so the
   * client is deliberately not the one deciding whether this is an add or a
   * remove — Odoo does, and says which way it went. The reply is not used to
   * redraw: the thread is re-read, because a reaction changes a message that
   * is already in it.
   */
  react: (alertId: number, messageId: number, emoji: string) =>
    http.post<{ ok: true; on: boolean }>('/api/andon/react', { alertId, messageId, emoji }),

  photo: (alertId: number, data: string, name?: string) =>
    http.post<{ ok: true; attachmentId: number }>('/api/andon/photo', { alertId, data, name }),

  /**
   * Put one file on the alert. IT SAYS NOTHING.
   *
   * Uploading and speaking are two steps. This returns an id; `message` takes
   * a list of them and makes the bubble. That is what lets four photos and a
   * caption be ONE message rather than five rows whose only link was a
   * filename inside a sentence — and it means each file's progress starts the
   * moment it is picked, instead of four of them queueing behind one request
   * that has to be retried whole if the tablet drops off the wifi.
   *
   * `kind` is a hint about the container, never a permission: the server
   * sniffs the bytes and refuses anything not on its allow-list whatever this
   * says. `seconds` is the recording's length as the device timed it — the
   * server cannot read it from the container without decoding it, and the
   * thread has to render "0:14" before it downloads a byte.
   */
  attach: (input: { alertId: number; kind: 'photo' | 'audio' | 'video' | 'file'; data: string; name?: string; seconds?: number }) =>
    http.post<{ ok: true; attachment: ThreadAttachmentVM }>('/api/andon/attach', input),

  /**
   * Where one attachment's bytes live.
   *
   * A URL rather than a fetch, because everything that consumes it — `<img>`,
   * `<video>`, `<a>` — wants a src and does its own ranged, resumable,
   * cache-aware loading far better than any hand-written fetch would. The
   * cookie rides along because it is same-origin.
   */
  attachmentUrl: (alertId: number, attachmentId: number) => `/api/andon/alert/${alertId}/attachment/${attachmentId}`,

  // ── responder ────────────────────────────────────────────────────────────
  //
  // No `inbox`: the responder's list and the triage board are one board, read
  // once from `board` below and filtered in the browser.

  /**
   * Who can be named with an @.
   *
   * A different list from `responders`, which is who an andon can be HANDED to.
   * The two were one endpoint, which is why the picker could offer the routing
   * table and not the maintenance fitter twenty metres away.
   */
  mentionRoster: () => http.get<{ people: RosterPerson[] }>('/api/andon/roster').then((r) => r.people),

  /**
   * Who a raise will reach, before it is raised.
   *
   * The raise used to show the tile's static routes string — a description of a
   * routing table rather than an answer about this station and this problem. The
   * server resolves it through the same function the raise itself calls a second
   * later, so the preview cannot drift from what actually happens.
   */
  routingPreview: (type: AlertTypeKey, workcentreId: number | null) =>
    http.get<RoutingPreviewVM>(
      `/api/andon/routing-preview?type=${encodeURIComponent(type)}` +
        (workcentreId != null ? `&workcentreId=${workcentreId}` : ''),
    ),

  // ── follow-ups ──────────────────────────────────────────────────────────
  //
  // `/andon/task`, not `/andon/ask` — that path belongs to the retrieval
  // endpoint and has since before any of this.

  /**
   * Ask somebody to do something. One line, one person, one date.
   *
   * `login` and not a name, the same rule a mention follows: names are not
   * unique, and a typed one would silently ask the wrong Ben.
   */
  askFollowUp: (alertId: number, summary: string, login: string, due: string) =>
    http.post<{ alert: AndonAlertVM; taskId: number | null }>('/api/andon/task', { alertId, summary, login, due }),

  /** The assignee's own move. */
  taskDone: (alertId: number, taskId: number, feedback: string | null = null) =>
    http.post<{ alert: AndonAlertVM }>('/api/andon/task/done', { alertId, taskId, feedback }).then((r) => r.alert),

  /** Not me — pass it on. It stays OPEN: passing it on is not answering it. */
  taskHandOn: (alertId: number, taskId: number, login: string) =>
    http.post<{ alert: AndonAlertVM }>('/api/andon/task/hand-on', { alertId, taskId, login }).then((r) => r.alert),

  /** Whoever asked withdraws the ask. The reason is required. */
  taskDrop: (alertId: number, taskId: number, feedback: string) =>
    http.post<{ alert: AndonAlertVM }>('/api/andon/task/drop', { alertId, taskId, feedback }).then((r) => r.alert),

  tasks: () => http.get<TasksVM>('/api/andon/tasks'),

  /**
   * Withdraw it, with a reason.
   *
   * The reason is required by the schema and required again in Odoo under the
   * row lock — "cancelled" with nothing behind it is the 58.3%-blank problem in
   * a new place. `duplicateOf` is honoured only on the `duplicate` reason and
   * ignored on every other, so a stale id costs nothing.
   */
  cancel: (alertId: number, reason: string, note: string | null, duplicateOf: number | null) =>
    http
      .post<{ alert: AndonAlertVM }>('/api/andon/cancel', { alertId, reason, note, duplicateOf })
      .then((r) => r.alert),

  /**
   * TAKE IT, WITH AN ETA. Where the SLA clock stops.
   *
   * One call for what used to be two. `acknowledge` put a name and a time on
   * the andon; this said "I am engaged". Nobody ever made the second move —
   * somebody who has taken an andon and named a time is already dealing with
   * it — so the stages merged and so did the calls. Taking it claims, and
   * there is no separate claim call from this app: a name on a record with no
   * time attached promised the operator nothing, and it was the one action
   * that could silence every other responder without committing anybody to
   * arriving.
   *
   * `etaMinutes` is any minute count. `basis: 'end_of_shift'` sends NO
   * minutes: the shift calendar is in Odoo, CNC (Biesse) knocks off at a
   * different hour from GRC Pouring, and a figure invented here would be
   * indistinguishable on the record from one a responder chose.
   *
   * `resolveEtaMinutes` is the fix estimate and is almost always null. The
   * sheet asks when you will BE there, because that is what the raiser is
   * waiting on; the estimate is there for somebody who already knows.
   */
  respond: (
    alertId: number,
    etaMinutes: number | null,
    basis: 'chosen' | 'end_of_shift' = 'chosen',
    resolveEtaMinutes: number | null = null,
  ) =>
    http
      .post<{ alert: AndonAlertVM }>('/api/andon/responding', { alertId, etaMinutes, basis, resolveEtaMinutes })
      .then((r) => r.alert),

  /**
   * Put it back on the board. The only backwards move there is.
   *
   * The clock RESUMES from the raise rather than restarting, so this cannot
   * buy anybody a fresh window while the operator is still standing there.
   */
  release: (alertId: number, why: string | null = null) =>
    http.post<{ alert: AndonAlertVM }>('/api/andon/release', { alertId, why }).then((r) => r.alert),

  /** Push out the fix estimate. Counted on the record, so say why if you can. */
  reviseResolveEta: (alertId: number, resolveEtaMinutes: number, why: string | null) =>
    http
      .post<{ alert: AndonAlertVM }>('/api/andon/resolve-eta', { alertId, resolveEtaMinutes, why })
      .then((r) => r.alert),

  handOver: (alertId: number, toLogin: string) =>
    http.post<{ alert: AndonAlertVM }>('/api/andon/handover', { alertId, toLogin }).then((r) => r.alert),

  escalate: (alertId: number) =>
    http.post<{ alert: AndonAlertVM }>('/api/andon/escalate', { alertId }).then((r) => r.alert),

  responders: () =>
    http
      .get<{ people: Array<{ login: string; name: string; uid: number | null }> }>('/api/andon/responders')
      .then((r) => r.people),

  // ── triage and insight ───────────────────────────────────────────────────
  board: () => http.get<BoardVM>('/api/andon/board'),
  insight: () => http.get<InsightVM>('/api/andon/insight'),

  // ── lookups ──────────────────────────────────────────────────────────────
  stations: () =>
    http.get<{ workcentres: AndonWorkcentreVM[]; flowlines: AndonFlowlineVM[] }>('/api/andon/stations'),

  pods: (query: { term?: string; scan?: string }) => {
    const params = new URLSearchParams();
    if (query.term) params.set('term', query.term);
    if (query.scan) params.set('scan', query.scan);
    return http.get<{ pods: AndonPodVM[] }>(`/api/andon/pods?${params}`).then((r) => r.pods);
  },

  // ── notifications ────────────────────────────────────────────────────────
  // `devices` is how many subscriptions the SERVER holds. The browser cannot
  // work that out for itself, and it is the one push failure that looks
  // perfectly healthy from here — see `lib/pushHealth`.
  notifications: () =>
    http.get<{ items: NotificationVM[]; publicKey: string | null } & PushServerView>('/api/andon/notifications'),
  clearNotifications: () => http.post<{ ok: true }>('/api/andon/notifications/clear'),

  subscribePush: (sub: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    http.post<{ ok: true }>('/api/andon/push/subscribe', sub),

  unsubscribePush: (endpoint: string) => http.post<{ ok: true }>('/api/andon/push/unsubscribe', { endpoint }),

  /** Send a real notification to your own devices, and report what happened. */
  testPush: () => http.post<PushTestVM>('/api/andon/push/test'),
};
